/**
 * User Preferences Management with Cookies
 * Saves and loads user settings like skin type and outdoor duration
 */

// Default user preferences
const DEFAULT_PREFERENCES = {
  skinType: 3, // Type III - Medium
  duration: 60, // 60 minutes
  surfaceType: 'none', // Default surface type
  cloudCover: 'clear', // Default cloud cover,
  otherFactors: {
    pregnant: false,
    breastfeeding: false,
    skinCancer: false,
    acne: false,
    rosacea: false,
    eczema: false,
    photosensitizing: false,
    antibiotics: false,
    retinoids: false,
    chemotherapy: false,
    immunosuppressants: false,
    diuretics: false,
    tattoos: false,
    recentProcedures: false,
    fairHair: false,
    lightEyes: false,
    freckles: false,
    moles: false
  }
};

// Cookie settings
const COOKIE_NAME = 'sunscreen_preferences';
const COOKIE_EXPIRY_DAYS = 365; // 1 year

/**
 * Set a cookie with expiration
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Days until expiration
 */
const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  
  return null;
};

/**
 * Save user preferences to cookies
 * @param {Object} preferences - User preferences object
 * @param {number} preferences.skinType - Fitzpatrick skin type (1-6)
 * @param {number} preferences.duration - Outdoor duration in minutes
 * @param {string} preferences.surfaceType - Surface type (snow, water, sand, grass, concrete, etc.)
 * @param {string} preferences.cloudCover - Cloud cover type (clear, scattered, broken, overcast)
 * @param {Object} preferences.otherFactors - Other risk factors (medical, medications, physical)
 */
export const saveUserPreferences = (preferences) => {
  try {
    const prefsString = JSON.stringify(preferences);
    setCookie(COOKIE_NAME, prefsString, COOKIE_EXPIRY_DAYS);
    console.log('💾 User preferences saved:', preferences);
  } catch (error) {
    console.error('Error saving user preferences:', error);
  }
};

/**
 * Load user preferences from cookies
 * @returns {Object} User preferences with defaults as fallback
 */
export const loadUserPreferences = () => {
  try {
    const prefsString = getCookie(COOKIE_NAME);
    
    if (prefsString) {
      const preferences = JSON.parse(prefsString);
      
      // Validate and sanitize loaded preferences
      const validatedPrefs = {
        skinType: Math.max(1, Math.min(6, preferences.skinType || DEFAULT_PREFERENCES.skinType)),
        duration: Math.max(5, Math.min(480, preferences.duration || DEFAULT_PREFERENCES.duration)),
        surfaceType: preferences.surfaceType || DEFAULT_PREFERENCES.surfaceType,
        cloudCover: preferences.cloudCover || DEFAULT_PREFERENCES.cloudCover,
        otherFactors: {
          ...DEFAULT_PREFERENCES.otherFactors,
          ...(preferences.otherFactors || {})
        }
      };
      
      console.log('📂 User preferences loaded:', validatedPrefs);
      return validatedPrefs;
    }
  } catch (error) {
    console.error('Error loading user preferences:', error);
  }
  
  // Return defaults if no preferences found or error occurred
  console.log('🔧 Using default preferences:', DEFAULT_PREFERENCES);
  return DEFAULT_PREFERENCES;
};

/**
 * Clear user preferences (remove cookie)
 */
export const clearUserPreferences = () => {
  try {
    setCookie(COOKIE_NAME, '', -1); // Set expiry to past date
    console.log('🗑️ User preferences cleared');
  } catch (error) {
    console.error('Error clearing user preferences:', error);
  }
};

/**
 * Check if user preferences exist
 * @returns {boolean} True if preferences are saved
 */
export const hasUserPreferences = () => {
  return getCookie(COOKIE_NAME) !== null;
};